import { useStoreLogger } from '@/logger.ts'
import type { ComposerTranslation } from 'vue-i18n'

// Message key is aoe.checkout.paymentMethods.<SystemKeyword>; falls back to the payment option's configured name
export function TranslatePaymentMethodName(t: ComposerTranslation, systemKeyword: string, name: string): string {
  const translationKey = `checkout.paymentMethods.${systemKeyword}`
  const translated = t(translationKey)
  // If translation key doesn't exist, t() returns the key itself
  return translated !== translationKey ? translated : name
}

// Applied payments identify PayPal and EPS by attributes.type rather than SystemKeyword, same as translatePaymentMethod
export function TranslateAppliedPaymentName(t: ComposerTranslation, systemKeyword: string, name: string, paymentType?: string): string {
  const type = paymentType?.toLowerCase()
  if (type === 'paypalaccount') return TranslatePaymentMethodName(t, 'PayPal', name)
  if (type === 'eps') return TranslatePaymentMethodName(t, 'EPS', name)
  return TranslatePaymentMethodName(t, systemKeyword, name)
}

export function GetUniqueArray(array: string[]): string[] {
  const uniqueArray: string[] = []
  array.forEach(item => {
    if (!uniqueArray.includes(item)) {
      uniqueArray.push(item)
    }
  })
  return uniqueArray
}

export function SetIsLoading(isloading : boolean) {
  const logger = useStoreLogger()
  logger.debug('SetIsLoading', isloading)
  if (isloading === true)
  {
    logger.debug('SetIsLoading', 'aoeloading')
    window.postMessage({ messageType: 'aoeloading' })
  }
  else
  {
    logger.debug('SetIsLoading', 'aoeloaded')
    window.postMessage({ messageType: 'aoeloaded' })
  }
}

// Cache for script loading promises to avoid duplicate loads
const scriptLoadPromises = new Map<string, Promise<void>>()

/**
 * Adds a JavaScript file to the document head and returns a Promise
 * that resolves when the script has loaded.
 *
 * @param jsname - The URL of the script to load
 * @returns Promise that resolves when script is loaded, rejects on error
 */
export function AddJavascriptToHeader(jsname: string): Promise<void> {
  // Return cached promise if script is already loading/loaded
  if (scriptLoadPromises.has(jsname)) {
    return scriptLoadPromises.get(jsname)!
  }

  // Check if script already exists in DOM
  const existingScript = document.querySelector(`script[src="${jsname}"]`)
  if (existingScript) {
    const resolvedPromise = Promise.resolve()
    scriptLoadPromises.set(jsname, resolvedPromise)
    return resolvedPromise
  }

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = jsname
    script.async = true

    script.onload = () => {
      resolve()
    }

    script.onerror = () => {
      scriptLoadPromises.delete(jsname) // Allow retry on error
      reject(new Error(`Failed to load script: ${jsname}`))
    }

    document.head.appendChild(script)
  })

  scriptLoadPromises.set(jsname, promise)
  return promise
}

// PayPal's SDK renders its button asynchronously into '#braintreepaypalbutton'
// (see the AoeBraintreePaypal* classes). Until that render resolves, callers
// show a same-sized placeholder (#braintreepaypalbutton-placeholder) so the
// layout doesn't jump. This hides that placeholder once the real button is ready.
export function HidePayPalButtonPlaceholder(): void {
  if (typeof document === 'undefined') return
  const placeholder = document.getElementById('braintreepaypalbutton-placeholder')
  if (placeholder) {
    placeholder.style.display = 'none'
  }
}
